import { useEffect } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useWebPushNotifications } from '@hooks/useWebPushNotifications';
import { useWebPushPermissionPrompt } from '@hooks/useWebPushPermissionPrompt';
import { usePushPromptChoreography } from '@hooks/usePushPromptChoreography';
import { useSwNotificationNavigate } from '@hooks/useSwNotificationNavigate';
import { WebPushPermissionDrawer } from './WebPushPermissionDrawer';
import { OneSignalPushInitializer } from './OneSignalPushInitializer';
import { logger } from '@utils/logger';
import { isOneSignalPushOwner } from '@lib/onesignal';

/**
 * Chooses the push pipeline for non-native platforms.
 *
 * `isOneSignalPushOwner()` is env-static per build (it reads
 * VITE_ONESIGNAL_PUSH_OWNER + VITE_ONESIGNAL_APP_ID, baked in at build time), so
 * the branch never flips within a session — mounting one initializer or the
 * other is safe and creates no conditional-hook hazard (neither child's hooks
 * are ever added/removed mid-lifecycle). Today the flag is off in every
 * environment, so LegacyWebPushInitializer renders and behaviour is unchanged.
 */
export function WebPushInitializer() {
  return isOneSignalPushOwner() ? <OneSignalPushInitializer /> : <LegacyWebPushInitializer />;
}

/**
 * Legacy self-managed VAPID push initializer (the pipeline in production today).
 *
 * - Auto-subscribes returning users who already granted permission
 * - Shows permission prompt drawer after a delay for new users
 * - Handles NOTIFICATION_NAVIGATE messages from the service worker
 */
function LegacyWebPushInitializer() {
  const { isAuthenticated } = useAuth();
  const webPush = useWebPushNotifications();
  const prompt = useWebPushPermissionPrompt(webPush);
  useSwNotificationNavigate();

  // Auto-subscribe if permission was already granted (returning user)
  useEffect(() => {
    if (!webPush.isSupported || !isAuthenticated) return;
    if (
      webPush.permission === 'granted' &&
      !webPush.isSubscribed &&
      !webPush.isLoading &&
      !webPush.isSubscribing
    ) {
      logger.info('Web push: auto-subscribing (permission already granted)');
      void webPush.subscribe();
    }
    // Only run when auth/permission/subscription state settles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    webPush.isSupported,
    isAuthenticated,
    webPush.permission,
    webPush.isSubscribed,
    webPush.isLoading,
    webPush.isSubscribing,
  ]);

  // Shared opt-in choreography (10s delay + cross-prompt coordination),
  // identical to the OneSignal initializer by construction.
  usePushPromptChoreography(prompt, isAuthenticated);

  if (!webPush.isSupported) return null;

  return <WebPushPermissionDrawer prompt={prompt} />;
}
